import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const CraftsmanADUDesignHalfMoonBay = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Craftsman ADU Design & Construction in Half Moon Bay",
    "description": "Create charming Craftsman ADUs in Half Moon Bay featuring handcrafted details, natural materials, and coastal cottage appeal.",
    "url": "https://www.udaadu.com/craftsman-adu-design-half-moon-bay",
    "mainEntity": {
      "@type": "Service",
      "name": "Craftsman ADU Design & Construction",
      "provider": {
        "@type": "Organization",
        "name": "UDA ADU"
      },
      "areaServed": "Half Moon Bay, CA"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/craftsman-adu-design-half-moon-bay" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Craftsman ADU Design & Construction in Half Moon Bay
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Create charming Craftsman ADUs in Half Moon Bay featuring handcrafted details, natural materials, and coastal cottage appeal.
          </p>
        </div>

        {/* Call to Action */}
        <div className="text-center mb-16">
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Get Free Consultation
          </Button>
          <Button asChild variant="outline" size="lg" className="text-white border-white hover:bg-white hover:text-black">
            <Link to="/adu-calculator">Calculate Costs</Link>
          </Button>
        </div>

        {/* Why Choose Craftsman ADUs */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Why Choose Craftsman ADUs in Half Moon Bay?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Coastal Cottage Charm</h3>
              <p className="text-muted-foreground">
                Craftsman designs complement Half Moon Bay's rural charm with authentic details and natural materials.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Handcrafted Quality</h3>
              <p className="text-muted-foreground">
                Artisan details and quality construction create ADUs that stand the test of time and weather.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Natural Integration</h3>
              <p className="text-muted-foreground">
                Natural materials and earth-tone palettes blend seamlessly with Half Moon Bay's rural coastal landscape and artistic community.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Craftsman ADU Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-white">Exterior Elements</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Low-pitched gable roofs with wide eaves</li>
                <li>• Exposed rafter tails and decorative brackets</li>
                <li>• Natural stone and wood siding combinations</li>
                <li>• Covered front porches with tapered columns</li>
                <li>• Multi-pane windows with decorative trim</li>
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-white">Interior Design</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Exposed beam ceilings and wainscoting</li>
                <li>• Built-in cabinetry and window seats</li>
                <li>• Hardwood floors and natural stone accents</li>
                <li>• Mission-style lighting and hardware</li>
                <li>• Cozy reading nooks and breakfast areas</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Local Considerations */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Half Moon Bay Building Considerations</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Rural Character Preservation</h3>
              <p className="text-muted-foreground">
                Half Moon Bay values its rural character. Craftsman designs naturally align with community preferences for traditional, human-scale architecture.
              </p>
            </div>
            <div className="p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Coastal Environment Adaptations</h3>
              <p className="text-muted-foreground">
                Craftsman materials are selected for coastal durability while maintaining authentic appearance. Proper sealing and maintenance ensure longevity.
              </p>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Investment Range</h2>
          <div className="text-center p-8 border border-border rounded-lg">
            <p className="text-2xl font-bold mb-4 text-white">$260,000 - $420,000</p>
            <p className="text-muted-foreground">
              Craftsman ADUs require skilled craftsmanship and quality materials. Custom millwork and built-ins add value and character.
            </p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-8 text-white">Start Your Craftsman ADU Project</h2>
          <p className="text-xl mb-8 text-muted-foreground max-w-3xl mx-auto">
            Ready to add artisan charm to your Half Moon Bay property? Let's create your perfect coastal cottage ADU.
          </p>
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Schedule Consultation
          </Button>
          <Button asChild variant="outline" size="lg" className="text-white border-white hover:bg-white hover:text-black">
            <Link to="/adu-calculator">Get Cost Estimate</Link>
          </Button>
        </section>

        {/* Internal Links */}
        <section className="text-center">
          <h3 className="text-2xl font-semibold mb-6 text-white">Explore Other ADU Styles</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild variant="outline">
              <Link to="/cottage-adu-design-half-moon-bay">Cottage ADUs</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/farmhouse-adu-design-half-moon-bay">Farmhouse ADUs</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/rustic-adu-design-half-moon-bay">Rustic ADUs</Link>
            </Button>
          </div>
        </section>
      </main>

      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default CraftsmanADUDesignHalfMoonBay;